/** The alerting pages' routes, so a link and its route never drift apart. */

export const RULES_PATH = "/alerts/rules";
export const TARGETS_PATH = "/alerts/targets";
export const NEW_RULE_PATH = `${RULES_PATH}/new`;

export function rulePath(id: string): string {
  return `${RULES_PATH}/${id}`;
}

/** The rule page's Incidents tab: every time the rule has fired. */
export function ruleIncidentsPath(id: string): string {
  return `${rulePath(id)}/incidents`;
}

/** One fired alert, keyed by the FIRING event that opened it. */
export function incidentPath(ruleId: string, eventId: string): string {
  return `${ruleIncidentsPath(ruleId)}/${eventId}`;
}

/** The observability page, where the Traces and Logs views live. */
const OBSERVABILITY_PATH = "/observability";

/** A moment as the observability page's `tsMin` and `tsMax` take it. */
const nanoseconds = (when: Date) => `${String(when.getTime())}000000`;

/**
 * The Traces view narrowed to a span and, when given, a service. The page
 * opens Traces whenever `service` is in the address.
 */
export function tracesPath(
  span: { from: Date; to: Date },
  service?: string,
): string {
  const params = new URLSearchParams({
    tsMin: nanoseconds(span.from),
    tsMax: nanoseconds(span.to),
  });
  if (service !== undefined) params.set("service", service);
  return `${OBSERVABILITY_PATH}?${params.toString()}`;
}

/**
 * Whether the Logs view can be narrowed to this service. It reads
 * `services` as a comma-separated list, so a name with a comma would come
 * apart into names that match nothing.
 */
export const logsCanNarrowTo = (service: string) => !service.includes(",");

/**
 * The Logs view narrowed to a span and, when it can be, a service. Logs
 * take the service as `services`; `service` would open Traces instead.
 */
export function logsPath(
  span: { from: Date; to: Date },
  service?: string,
): string {
  const params = new URLSearchParams({
    tab: "logs",
    tsMin: nanoseconds(span.from),
    tsMax: nanoseconds(span.to),
  });
  if (service !== undefined && logsCanNarrowTo(service)) {
    params.set("services", service);
  }
  return `${OBSERVABILITY_PATH}?${params.toString()}`;
}
